"use client";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Network,
  Clock,
  AlertOctagon,
} from "lucide-react";

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
}

interface TrafficData {
  total_sent: number;
  total_delivered: number;
  total_pending: number;
  total_failed: number;
  total_absent_from_network: number;
  invalid: number;
  total_delivered_to_network: number;
}

interface PieChartProps {
  trafficData: TrafficData;
}

const TrafficPieChart = ({ trafficData }: PieChartProps) => {
  const chartData: ChartDataItem[] = [
    { name: "total_sent", value: trafficData.total_sent, color: "#6b7280" },
    {
      name: "total_delivered",
      value: trafficData.total_delivered,
      color: "#10b981",
    },
    {
      name: "total_pending",
      value: trafficData.total_pending,
      color: "#3b82f6",
    },
    { name: "total_failed", value: trafficData.total_failed, color: "#ef4444" },
    {
      name: "total_absent_from_network",
      value: trafficData.total_absent_from_network,
      color: "#f59e0b",
    },
    { name: "invalid", value: trafficData.invalid, color: "#8b5cf6" },
    {
      name: "total_delivered_to_network",
      value: trafficData.total_delivered_to_network,
      color: "#06b6d4",
    },
  ];

  // Format numbers
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  // Custom tooltip formatter for charts
  const customTooltipFormatter = (value: number): string => {
    return formatNumber(value);
  };

  // Display names for the legend
  const getDisplayName = (name: string): string => {
    const displayNames: Record<string, string> = {
      total_sent: "Total Sent",
      total_delivered: "Total Delivered",
      total_pending: "Total Pending",
      total_failed: "Total Failed",
      total_absent_from_network: "Total Absent From Network",
      invalid: "Invalid",
      total_delivered_to_network: "Total Delivered To Network",
    };
    return displayNames[name] || name;
  };

  const getIcon = (index: number) => {
    const icons = [
      <Users key="user-icon" className="h-5 w-5 text-white" />,
      <CheckCircle key="check-icon" className="h-5 w-5 text-white" />,
      <Clock key="clock-icon" className="h-5 w-5 text-white" />,
      <XCircle key="x-icon" className="h-5 w-5 text-white" />,
      <AlertTriangle key="alert-icon" className="h-5 w-5 text-white" />,
      <AlertOctagon key="octagon-icon" className="h-5 w-5 text-white" />,
      <Network key="network-icon" className="h-5 w-5 text-white" />,
    ];
    return icons[index];
  };

  return (
    <div className="overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-8 w-1 bg-red-600 rounded-full"></div>
        <h2 className="text-xl font-semibold text-gray-900">
          Traffic Distribution
        </h2>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any) =>
                customTooltipFormatter(value as number)
              }
              labelFormatter={getDisplayName}
              contentStyle={{
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            />
            <Legend
              formatter={getDisplayName}
              wrapperStyle={{ paddingTop: "20px" }}
              iconType="circle"
              layout="horizontal"
              align="center"
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrafficPieChart;
